// src/hooks/useWizardActiveDetection.ts
//
// PR9 final integração: detecta wizard 'paused' no mount e oferece resume/start novo.
// Pra usar: const { activeWizard, dismiss, startNew } = useWizardActiveDetection({ wizardSlug, channel, channelUserId });

import { useEffect, useState } from 'react';
import { api, type WizardExecution } from '@/lib/api';

export interface UseWizardActiveDetectionParams {
  wizardSlug?: string;
  channel?: string;
  channelUserId?: string;
  enabled?: boolean;
}

export function useWizardActiveDetection(params: UseWizardActiveDetectionParams) {
  const [activeWizard, setActiveWizard] = useState<WizardExecution | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.enabled === false) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) setLoading(true);
    });
    const filters: Parameters<typeof api.findActiveWizard>[0] = {};
    if (params.wizardSlug) filters.wizard_slug = params.wizardSlug;
    if (params.channel) filters.channel = params.channel;
    if (params.channelUserId) filters.channel_user_id = params.channelUserId;

    api
      .findActiveWizard(filters)
      .then((res) => {
        // Mostra prompt apenas se status='paused' (foi abandonado/refresh)
        if (res.wizard_execution?.status === 'paused') {
          setActiveWizard(res.wizard_execution);
        } else {
          setActiveWizard(null);
        }
      })
      .catch(() => setActiveWizard(null))
      .finally(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [params.wizardSlug, params.channel, params.channelUserId, params.enabled]);

  const dismiss = () => setActiveWizard(null);

  const resume = async () => {
    if (!activeWizard) return null;
    const result = await api.resumeWizard(activeWizard.id);
    setActiveWizard(null);
    return result;
  };

  const abandon = async () => {
    if (!activeWizard) return null;
    const result = await api.abandonWizard(activeWizard.id, 'user_chose_new');
    setActiveWizard(null);
    return result;
  };

  return { activeWizard, loading, dismiss, resume, abandon };
}
